// A visit's site plan: made from Nearmap (centred on the iPad, framed, saved)
// or an image the measurer picks (Photos or Files), saved to the job's
// SharePoint site — the one copy — and kept on the device so it shows with no
// signal. Making one needs signal; pins don't (docs/CONDITION-REGION.md).
// Its copies, with the pins or the access lines drawn on, are marked-up-plan.ts and access-plan.ts.
import { planKeyOf } from '@higgins/condition-library';
import { ApiError } from '@higgins/utils';
import { getNearmapTile, getSitePlanContent, uploadSitePlanApi } from './api';
import { getSitePlanImage, patchFields, saveSitePlanImage, type DeviceRecord } from './device-db';
import { clearAccessLines, clearPins, placePin } from './condition-records';
import { preparePhoto } from './photo-resize';
import { pinFor, tileKey, tilesFor, wholeBearing, type PlanFrame } from './site-plan-math';

const JSON_VERSION = 1;
const JPEG_QUALITY = 0.85;

export type PlanSource = 'Nearmap' | 'SharePoint' | 'Photos';

/** hig_siteplan, v1 (api condition-json-check.ts). */
export interface SitePlanDoc {
  source: PlanSource;
  driveItemId: string;
  webUrl: string;
  width: number;
  height: number;
  /** A Nearmap plan only: where it is, so a GPS position converts to a point on it. */
  mercator?: { zoom: number; left: number; top: number };
  /** When it was made. A remade plan replaces the same file (the same driveItemId), so this tells them apart. */
  savedOn?: string;
  /** The last marked-up copy (pins drawn on): its link, when, and the fingerprint of the pins it shows (marked-up-plan.ts). */
  markedUp?: { webUrl: string; savedOn: string; pins: string };
  /** The last access copy (access lines drawn on, no pins): its link, when, and the fingerprint of the lines it shows (access-plan.ts). */
  access?: { webUrl: string; savedOn: string; lines: string };
}

/** Which plan this is, for the device's copy of its image (and, with the server, the copies' fingerprints). */
export const planVersion = (plan: SitePlanDoc): string => planKeyOf(plan);

export function parseSitePlan(raw: unknown): SitePlanDoc | null {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const doc = JSON.parse(raw) as SitePlanDoc & { schemaVersion?: number };
    return doc.schemaVersion === JSON_VERSION && typeof doc.driveItemId === 'string' ? doc : null;
  } catch {
    return null;
  }
}

/** A Nearmap plan's frame, for placing pins from GPS; null for a plan without coordinates. */
function frameOf(plan: SitePlanDoc): PlanFrame | null {
  return plan.mercator ? { ...plan.mercator, width: plan.width, height: plan.height } : null;
}

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** An image from a URL (a data URL, or an object URL), once it has loaded. */
export function loadImageUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('An image for the plan couldn’t be read.'));
    img.src = url;
  });
}

const loadImage = (blob: Blob): Promise<HTMLImageElement> => loadImageUrl(URL.createObjectURL(blob));

/** A canvas as the JPEG a plan is saved as. */
export const toJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The plan couldn’t be made.'))), 'image/jpeg', JPEG_QUALITY));

/** One tile's image: the one already on screen (by object URL) if there is one, else fetched. Null when Nearmap has none. */
async function tileImage(tile: { z: number; x: number; y: number }, onScreen: Readonly<Record<string, string>>): Promise<HTMLImageElement | null> {
  const shown = onScreen[tileKey(tile)];
  try {
    if (shown) return await loadImageUrl(shown);
    const img = await loadImage(await getNearmapTile(tile.z, tile.x, tile.y));
    URL.revokeObjectURL(img.src);
    return img;
  } catch (err) {
    console.warn('Nearmap tile missing', tile, err);
    return null;
  }
}

/**
 * Draw the tiles covering a frame into one JPEG. Tiles already on screen
 * (`onScreen`, by tile key) are used as they are; only the rest are fetched,
 * all at once (#470: fetching each again, one by one, took minutes). A tile
 * with no imagery stays blank.
 */
export async function renderNearmap(frame: PlanFrame, onScreen: Readonly<Record<string, string>>): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = frame.width;
  canvas.height = frame.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This device can’t draw the plan.');
  ctx.fillStyle = '#d9d7d3';
  ctx.fillRect(0, 0, frame.width, frame.height);
  const tiles = tilesFor(frame);
  const images = await Promise.all(tiles.map((tile) => tileImage(tile, onScreen)));
  tiles.forEach((tile, i) => images[i] && ctx.drawImage(images[i], tile.dx, tile.dy));
  return toJpeg(canvas);
}

/** Save a plan: to SharePoint first (needs signal), then on the visit and the device. */
export async function savePlan(visit: DeviceRecord, image: Blob, source: PlanSource, size: { width: number; height: number }, frame?: PlanFrame): Promise<void> {
  const saved = await uploadSitePlanApi(visit.id, image);
  const doc: SitePlanDoc & { schemaVersion: number } = {
    schemaVersion: JSON_VERSION,
    source,
    driveItemId: saved.driveItemId,
    webUrl: saved.webUrl,
    ...size,
    ...(frame ? { mercator: { zoom: frame.zoom, left: frame.left, top: frame.top } } : {}),
    savedOn: new Date().toISOString(),
  };
  await saveSitePlanImage({ visitId: visit.id, version: planVersion(doc), dataUrl: await blobToDataUrl(image) });
  await patchFields(visit.id, { hig_siteplan: JSON.stringify(doc), hig_siteplansource: source });
  // A pin is a point on the old image: none means anything on the new one, hand-placed or not (PR #457).
  // On a Nearmap plan the photos with a position are pinned again from GPS (placeGpsPins); the rest are placed by hand.
  await clearPins(visit.jobId);
  // Access lines are drawn on the old image too: they're drawn again on the new one.
  await clearAccessLines(visit.jobId);
}

/** A picked image (Photos or Files) as a plan: resized like a photo, then saved. */
export async function savePickedPlan(visit: DeviceRecord, file: File): Promise<void> {
  const { image } = await preparePhoto(file);
  const img = await loadImage(image);
  const size = { width: img.naturalWidth, height: img.naturalHeight };
  URL.revokeObjectURL(img.src);
  await savePlan(visit, image, 'Photos', size);
}

/** The visit records a plan whose file is no longer on SharePoint (deleted or recycled there): only a new plan mends it. */
export class PlanFileMissing extends Error {}

/** The plan image to show: this device's copy, else SharePoint's (needs signal, then kept). Null when neither. */
export async function planImage(visitId: string, plan: SitePlanDoc): Promise<string | null> {
  const local = await getSitePlanImage(visitId);
  if (local && local.version === planVersion(plan)) return local.dataUrl;
  if (!navigator.onLine) return local?.dataUrl ?? null;
  const content = await getSitePlanContent(visitId).catch((err: unknown) => {
    throw err instanceof ApiError && err.code === 'FILE_MISSING' ? new PlanFileMissing(err.message) : err;
  });
  const dataUrl = await blobToDataUrl(content);
  await saveSitePlanImage({ visitId, version: planVersion(plan), dataUrl });
  return dataUrl;
}

/** Set the way a pin faces (#471): a whole bearing, 0–359, saved and synced like a pin's position. */
export const setPinBearing = (evidenceId: string, bearing: number): Promise<void> => patchFields(evidenceId, { hig_bearing: wholeBearing(bearing) });

/**
 * On a Nearmap plan, give every photo with a position and no pin yet its pin
 * from GPS. Pins placed by hand are never moved. Saved like any change.
 */
export async function placeGpsPins(plan: SitePlanDoc, evidence: readonly DeviceRecord[]): Promise<void> {
  const frame = frameOf(plan);
  if (!frame) return;
  for (const e of evidence) {
    const { hig_latitude: lat, hig_longitude: lon, hig_pinx: px } = e.fields;
    if (typeof px === 'number' || typeof lat !== 'number' || typeof lon !== 'number') continue;
    const pin = pinFor(lat, lon, frame);
    if (pin) await placePin(e.id, pin.x, pin.y, false);
  }
}
